import { ArrowLeft, ArrowRight, CheckCircle2, Eye, EyeOff, KeyRound, LockKeyhole, Mail } from "lucide-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { brandingAssetUrl, useBranding } from "@/hooks/useBranding";

const errorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((item) => item?.msg).filter(Boolean).join(" · ") || fallback;
  return fallback;
};

export default function LoginPage() {
  const [params] = useSearchParams();
  const resetToken = params.get("reset_token") || "";
  const [mode, setMode] = useState(resetToken ? "reset" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const { login } = useAuth();
  const { data: branding } = useBranding();
  const navigate = useNavigate();
  const logoUrl = brandingAssetUrl(branding?.logo_file_id) || "/marsem-logo.webp";
  const loginImageUrl = brandingAssetUrl(branding?.login_image_file_id) || "/marsem-course-hero.png";

  const submitLogin = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (error) {
      toast.error(errorMessage(error, "Nie udało się zalogować"));
    } finally {
      setBusy(false);
    }
  };

  const requestReset = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/password-reset/request", { email });
      setRequestSent(true);
    } catch {
      setRequestSent(true);
    } finally {
      setBusy(false);
    }
  };

  const confirmReset = async (event) => {
    event.preventDefault();
    if (password !== passwordConfirm) return toast.error("Hasła nie są takie same");
    setBusy(true);
    try {
      await api.post("/auth/password-reset/confirm", { token: resetToken, new_password: password });
      toast.success("Hasło zostało zmienione. Możesz się zalogować.");
      setPassword("");
      setPasswordConfirm("");
      setMode("login");
      navigate("/login", { replace: true });
    } catch (error) {
      toast.error(errorMessage(error, "Nie udało się zmienić hasła"));
    } finally {
      setBusy(false);
    }
  };

  const backToLogin = () => {
    setMode("login");
    setRequestSent(false);
    setPassword("");
    setPasswordConfirm("");
    if (resetToken) navigate("/login", { replace: true });
  };

  return (
    <div className="login-page">
      <div className="login-brand" data-testid="login-brand">
        <img className="login-art" src={loginImageUrl} alt="" />
        <div className="login-brand-overlay"><img className="login-custom-logo" src={logoUrl} alt="MARSEM LAB" /><p>Twoja przestrzeń do nauki, działania i realnych rezultatów.</p></div>
      </div>
      <section className="login-panel" data-testid="login-panel">
        <div className="login-form-wrap">
          {mode === "login" && <>
            <span className="eyebrow">STREFA UCZESTNIKA</span><h1>Witaj ponownie.</h1><p>Zaloguj się i kontynuuj swoją misję.</p>
            <form onSubmit={submitLogin} data-testid="login-form">
              <label>E-MAIL<input data-testid="login-email-input" value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="email" required /></label>
              <label>HASŁO<div className="password"><input data-testid="login-password-input" value={password} onChange={(event) => setPassword(event.target.value)} type={show ? "text" : "password"} autoComplete="current-password" required /><button type="button" title="Pokaż hasło" data-testid="toggle-password-button" onClick={() => setShow((current) => !current)}>{show ? <EyeOff /> : <Eye />}</button></div></label>
              <button className="forgot-password-link" type="button" onClick={() => setMode("recover")}>Nie pamiętasz hasła?</button>
              <button className="primary-btn" data-testid="login-submit-button" disabled={busy}>{busy ? "LOGOWANIE…" : "WEJDŹ DO PLATFORMY"}<ArrowRight size={18} /></button>
            </form>
            <div className="secure" data-testid="secure-login-info"><LockKeyhole size={14} /> Dostęp chroniony logowaniem</div>
          </>}

          {mode === "recover" && <>
            <button className="login-back" type="button" onClick={backToLogin}><ArrowLeft /> WRÓĆ DO LOGOWANIA</button>
            {!requestSent ? <>
              <span className="recovery-icon"><Mail /></span><span className="eyebrow">ODZYSKIWANIE DOSTĘPU</span><h1>Ustaw nowe hasło.</h1><p>Podaj e-mail konta. Wyślemy link ważny przez 30 minut.</p>
              <form onSubmit={requestReset} data-testid="password-reset-request-form"><label>E-MAIL<input value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="email" required /></label><button className="primary-btn" disabled={busy}>{busy ? "WYSYŁANIE…" : "WYŚLIJ LINK"}<ArrowRight /></button></form>
            </> : <div className="reset-sent"><CheckCircle2 /><span className="eyebrow">SPRAWDŹ SKRZYNKĘ</span><h1>Link jest w drodze.</h1><p>Jeśli podany adres należy do aktywnego konta, otrzymasz wiadomość z bezpiecznym linkiem do zmiany hasła.</p><button className="primary-btn" type="button" onClick={backToLogin}>WRÓĆ DO LOGOWANIA</button></div>}
          </>}

          {mode === "reset" && <>
            <button className="login-back" type="button" onClick={backToLogin}><ArrowLeft /> WRÓĆ DO LOGOWANIA</button>
            <span className="recovery-icon"><KeyRound /></span><span className="eyebrow">NOWE HASŁO</span><h1>Odzyskaj konto.</h1><p>Ustaw hasło składające się z co najmniej 8 znaków.</p>
            <form onSubmit={confirmReset} data-testid="password-reset-confirm-form">
              <label>NOWE HASŁO<div className="password"><input value={password} onChange={(event) => setPassword(event.target.value)} type={show ? "text" : "password"} autoComplete="new-password" minLength={8} required /><button type="button" title="Pokaż hasło" onClick={() => setShow((current) => !current)}>{show ? <EyeOff /> : <Eye />}</button></div></label>
              <label>POWTÓRZ HASŁO<input value={passwordConfirm} onChange={(event) => setPasswordConfirm(event.target.value)} type={show ? "text" : "password"} autoComplete="new-password" minLength={8} required /></label>
              <button className="primary-btn" disabled={busy}>{busy ? "ZAPISYWANIE…" : "USTAW NOWE HASŁO"}<ArrowRight /></button>
            </form>
          </>}
        </div>
      </section>
    </div>
  );
}
